import {Component, EventEmitter, OnInit, Output} from '@angular/core';
import {Router} from '@angular/router';
import {ROUTES} from 'src/app/shared/constants/routes';
import {UserProfile} from 'src/app/shared/models/user-profile';
import {ConfigService} from 'src/app/shared/services/config.service';
import {ApplicationProjectService} from './core/application-project/services/application-project.service';
import {IApplication} from './shared/models/application';
import {IUser, User} from './shared/models/user';
import {AuthenticationService} from './shared/services/authentication.service';
import {Constants} from './shared/constants/Constants';
import {ThemeService} from './shared/services/theme.service';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {UsersService} from "./core/user/services/users.service";
import {STEPPER_GLOBAL_OPTIONS} from "@angular/cdk/stepper";
import { SnackbarService } from './shared/services/snackbar.service';
import {LoginRequest} from './shared/models/auth';


@Component({
  selector: 'no-auth-access',
  styleUrls: ['./no-authentication-access.component.less'],
  templateUrl: './no-authentication-access.component.html',
  providers: [{
    provide: STEPPER_GLOBAL_OPTIONS, useValue: {showError: true}
  }]
})
export class NoAuthenticationAccessComponent implements OnInit {

  identifierFormGroup!: UntypedFormGroup;
  passwordFormGroup!: UntypedFormGroup;

  public navbarColor = "";

  public profile: UserProfile = UserProfile.ADMIN;

  public selectedApplicationsNames: string[] = [];

  public applicationsNames: string[] = [];
  public identifier = '';

  public userAllowed: IUser | undefined;

  canAccess = false;

  isAuthenticating = false;
  accountLocked = false;
  lockMessage = '';

  get UserProfile() {
    return UserProfile;
  }

  get Object() {
    return Object;
  }

  @Output() onUserFound = new EventEmitter<boolean>();

  constructor(public router: Router,
              private configService: ConfigService,
              public theme: ThemeService,
              private applicationProjectService: ApplicationProjectService,
              private authenticationService: AuthenticationService,
              private _formBuilder: UntypedFormBuilder,
              private userService: UsersService,
              private snackbarService: SnackbarService,
  ) {
  }

  public ngOnInit() {
    if (this.configService.isAuthEnabled()) {
      this.router.navigateByUrl(`/${ROUTES.CORE_PATH}`);
    } else {
      this.identifierFormGroup = this._formBuilder.group({
        identifier: ['', Validators.required]
      });

      this.passwordFormGroup = this._formBuilder.group({
        password: ['', [Validators.required, Validators.minLength(8)]]
      });

      this.applicationProjectService.getApplicationsNames().subscribe(applicationsNames => {
        this.applicationsNames = applicationsNames;
      });
    }
    this.navbarColor = this.theme.getColor(Constants.PackColor.MAIN_DARK);
  }

  public allowAccess() {
    this.isAuthenticating = true;
    this.accountLocked = false;
    
    const identifier = this.identifierFormGroup.value.identifier.toUpperCase();
    
    // Vérifier d'abord si le compte est verrouillé
    this.userService.isAccountLocked(identifier).subscribe({
      next: response => {
        if (!response.success) {
          // Compte verrouillé
          this.accountLocked = true;
          this.lockMessage = response.message;
          this.isAuthenticating = false;
          this.openErrorSnackBar(response.message);
        } else {
          // Compte actif, vérifier si l'utilisateur existe
          this.checkUserExists(identifier);
        }
      },
      error: error => {
        console.log(error);
        this.isAuthenticating = false;
        this.openErrorSnackBar("Erreur lors de la vérification du compte.");
      }
    });
  }

  private checkUserExists(identifier: string) {
    this.userService.getUserByIdentifier(identifier).subscribe({
      next: user => {
        this.userAllowed = user;
        this.isAuthenticating = false;
      },
      error: error => {
        console.log(error);
        this.isAuthenticating = false;
        if (error.status === 404) {
          this.openErrorSnackBar("Utilisateur non trouvé. Veuillez contacter votre administrateur.");
        } else {
          this.openErrorSnackBar("Vous n'êtes pas autorisé à accéder à l'application. Veuillez contacter votre administrateur.");
        }
      }
    });
  }

  public authenticateWithPassword() {
    if (!this.userAllowed) return;
    
    this.isAuthenticating = true;
    
    const loginRequest: LoginRequest = {
      identifier: this.identifierFormGroup.value.identifier.toUpperCase(),
      password: this.passwordFormGroup.value.password
    };
    
    this.userService.login(loginRequest).subscribe({
      next: response => {
        if (response.success) {
          this.canAccess = true;
          this.isAuthenticating = false;
          this.openSuccessSnackBar("Authentification réussie !");
          
          // Mettre à jour les informations utilisateur si retournées
          if (response.user) {
            this.userAllowed = response.user;
          }
        } else {
          this.isAuthenticating = false;
          // Afficher le message d'erreur du backend
          this.openErrorSnackBar(response.message || "Erreur lors de l'authentification.");
        }
      },
      error: error => {
        console.log(error);
        this.isAuthenticating = false;
        
        // Extraire le message d'erreur du backend
        let errorMessage = "Erreur lors de l'authentification.";
        
        if (error.error && error.error.message) {
          errorMessage = error.error.message;
          
          // Vérifier si le compte est verrouillé
          const messageLower = error.error.message.toLowerCase();
          if (messageLower.includes('verrouillé') || messageLower.includes('locked')) {
            this.accountLocked = true;
            this.lockMessage = error.error.message;
            // Réinitialiser pour revenir à l'étape 1 où le message s'affiche
            this.userAllowed = undefined;
            this.passwordFormGroup.reset();
          }
        } else if (error.status === 401) {
          errorMessage = "Identifiant ou mot de passe incorrect.";
        } else if (error.status === 400) {
          errorMessage = "Paramètres invalides ou mot de passe trop court.";
        }
        
        this.openErrorSnackBar(errorMessage);
      }
    });
  }

  public validateAccess() {
    const user: User = {
      lastName: this.userAllowed?.lastName,
      firstName: this.userAllowed?.firstName,
      identifier: this.userAllowed?.identifier,
      profile: this.profile,
      applications: this.selectedApplicationsNames.map(name => {
        return {name: name} as IApplication;
      }),
      nettingView: this.userAllowed?.nettingView,
    };
    sessionStorage.setItem('user', JSON.stringify(user));
    localStorage.setItem('debugMode', 'false');
    this.authenticationService.setUser(user);
    this.router.navigateByUrl(`/${ROUTES.CORE_PATH}`);
  }

  openErrorSnackBar(message: string) {
    this.snackbarService.openSnackBar(message, 'error-snack-bar');
  }

  openSuccessSnackBar(message: string) {
    this.snackbarService.openSnackBar(message);
  }
}
